<template>
  <div
    id="app-main-container"
    v-on:mousemove="onMousemove"
    v-on:mouseup="onMouseup"
  >
    <app-bar v-bind="externalProps">
      <template v-slot:default>
        <slot name="app-bar" v-bind="externalProps"></slot>
      </template>
    </app-bar>

    <resizable-frame
      class="flex-grow-1 overflow-y-hidden"
      ref="videoAndEditorViewResizableFrame"
      v-show="loadedConfig.showVideoAndEditorView"
      v-bind:bottom="loadedConfig.showVideoAndEditorView && loadedConfig.showTimelineView"
      v-bind:height-px.sync="videoAndEditorViewHeightPx"
    >
      <video-and-editor-view
        id="video-and-editor-view"
        ref="videoAndEditorView"
        v-bind:timeline-data-set="scattData.timelineDataSet"
        v-bind:video-title="videoTitle"
        v-bind:video-url="videoUrl"
        v-bind:video-time-range="videoTimeRange"
        v-bind:video-playback-rate.sync="loadedConfig.videoPlaybackRate"
        v-bind:video-volume-rate.sync="loadedConfig.videoVolumeRate"
        v-bind:video-muted.sync="loadedConfig.videoMuted"
        v-bind:is-loop-enabled.sync="isLoopEnabled"
        v-bind:loop-definition="loopDefinition"
        v-bind:timeline-marker="timelineMarker"
        v-bind:audio-only="audioOnly"
        v-bind:video-player-width-px.sync="loadedConfig.videoPlayerWidthPx"
        v-bind:selected-timeline-segment-ids="selectedTimelineSegmentIds"
        v-bind:annotator-id="annotatorId"
        v-bind:annotator-name="annotatorName"
        v-bind:editor-definitions="mergedEditorDefinitions"
        v-on:meta-data-loaded="onMetaDataLoaded"
        v-on:play-info-updated="onPlayInfoUpdated"
        v-on:playback-fixed="generatePlaybackHistory"
      >
      </video-and-editor-view>
    </resizable-frame>
    <timeline-list-view
      ref="timelineListView"
      v-show="loadedConfig.showTimelineView"
      v-bind:timeline-view-begin-min="timelineViewBeginMin"
      v-bind:timeline-view-end-max="timelineViewEndMax"
      v-bind:play-time="playTime"
      v-bind:is-video-playing="isVideoPlaying"
      v-bind:visible-timeline-data-set="visibleTimelineDataSet"
      v-bind:visible-timeline-data-idx-to-id="visibleTimelineDataIdxToId"
      v-bind:sorted-visible-segment-idx-to-id-maps="sortedVisibleSegmentIdxToIdMaps"
      v-bind:visible-segment-id-to-sorted-idx-maps="visibleSegmentIdToSortedIdxMaps"
      v-bind:waveform-digest="waveformDigest"
      v-bind:minimum-segment-duration="minimumSegmentDuration"
      v-bind:selected-timeline-segment-ids="selectedTimelineSegmentIds"
      v-bind:is-any-selected-timeline-segment-immutable="isAnySelectedTimelineSegmentImmutable"
      v-bind:is-loop-enabled.sync="isLoopEnabled"
      v-bind:is-mouse-mode.sync="loadedConfig.isMouseMode"
      v-bind:is-snap-enabled.sync="loadedConfig.isSnapEnabled"
      v-bind:loop-definition="loopDefinition"
      v-bind:timeline-marker="timelineMarker"
      v-bind:segment-data-types="mergedSegmentDataTypes"
      v-bind:timeline-title-width-px.sync="loadedConfig.timelineTitleWidthPx"
    >
    </timeline-list-view>
    <app-footer
      v-bind:tool-title="toolTitle"
      v-bind:messages="footerMessages"
      v-bind:version="toolSemanticVersionString"
    >
    </app-footer>
    <dialog-manager
      ref="dialogManager"
      v-bind:common-pre-initialize-callback="disableInputEventListener"
      v-bind:common-post-finalize-callback="enableInputEventListener"
    >
    </dialog-manager>
  </div>
</template>

<style scoped>
#app-main-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

#video-and-editor-view {
  height: 100%;
}

#horizontal-view-separator {
  border-top-width: 5px;
  border-top-style: solid;
  width: 100%;
  height: 5px;
  cursor: ns-resize;
  user-select: none;
}
</style>

<style src="./Scatt/styles/common.css" scoped></style>

<script>
import TimelineListView from './Scatt/components/TimelineListView.vue';
import VideoAndEditorView from './Scatt/components/VideoAndEditorView.vue';
import DialogManager from './Scatt/components/DialogManager.vue';
import AppBar from './Scatt/components/AppBar.vue';
import AppBarButton from './Scatt/components/AppBar/AppBarButton.vue';
import AppFooter, { FooterMessages } from './Scatt/components/AppFooter.vue';
import GeneralTextField from './Scatt/components/Parts/Inputs/GeneralTextField.vue';
import GeneralTextarea from './Scatt/components/Parts/Inputs/GeneralTextarea.vue';
import ResizableFrame from './Scatt/components/Parts/ResizableFrame.vue';
import VDataTableWithAutoScroll from './Scatt/components/Parts/VDataTableWithAutoScroll.vue';

import MarkerSegmentData                 from './Scatt/modules/segmentDataTypes/MarkerSegmentData.js';
import LabelSegmentData                  from './Scatt/modules/segmentDataTypes/LabelSegmentData.js';
import NoteSegmentData                   from './Scatt/modules/segmentDataTypes/NoteSegmentData.js';
import GeneralSegmentData                from './Scatt/modules/segmentDataTypes/GeneralSegmentData.js';
import PlaybackHistorySegmentData        from './Scatt/modules/segmentDataTypes/PlaybackHistorySegmentData.js';
import TimeSeries1DimensionalSegmentData from './Scatt/modules/segmentDataTypes/TimeSeries1DimensionalSegmentData.js';

import utils from './Scatt/modules/utils.js';
import TimeValue from './Scatt/modules/TimeValue.js';
import TimelineMarker from './Scatt/modules/TimelineMarker.js';
import LoopDefinition from './Scatt/modules/LoopDefinition.js';
import VideoTimeRange from './Scatt/components/VideoPlayer/modules/VideoTimeRange.js';
import providedFunctions from './Scatt/modules/providedFunctions.js';
import WaveformDigest from './Scatt/modules/WaveformDigest.js';
import referencePool, { Reference } from './Scatt/modules/referencePool.js';
import changeHistory from './Scatt/modules/changeHistory.js';
import {
  ScattData,
  TimelineDataSet,
  TimelineData,
  TimelineSegmentId,
  TimelineSegmentDataBase,
  TimelineSegmentContentDataBase,
} from './Scatt/modules/ScattData.js';
import logger from './Scatt/modules/logger.js';

import EditorDefinition                       from './Scatt/modules/EditorDefinition.js';
import { TimelineLabelEnumeratorDefinition }  from './Scatt/components/Editors/TimelineLabelEnumerator.vue';
import { NoteEditorDefinition }               from './Scatt/components/Editors/NoteEditor.vue';
import { TimelineSegmentInspectorDefinition } from './Scatt/components/Editors/TimelineSegmentInspector.vue';
import { GeneralEditorDefinition }            from './Scatt/components/Editors/GeneralEditor.vue';

import SegmentContentDataInputDefinition            from './Scatt/modules/SegmentContentDataInputDefinition.js'
import { NoteSegmentContentDataInputDefinition }    from './Scatt/components/Parts/Inputs/NoteSegmentContentDataInput.vue';
import { LabelSegmentContentDataInputDefinition }   from './Scatt/components/Parts/Inputs/LabelSegmentContentDataInput.vue';
import { MarkerSegmentContentDataInputDefinition }  from './Scatt/components/Parts/Inputs/MarkerSegmentContentDataInput.vue';
import { GeneralSegmentContentDataInputDefinition } from './Scatt/components/Parts/Inputs/GeneralSegmentContentDataInput.vue';

import packageJson from '../package.json';

const toolTitle = 'Scatt';

const [ toolSemanticVersionMajor, toolSemanticVersionMinor, toolSemanticVersionPatch ] = packageJson.version.split('.').map(Number);
const toolSemanticVersionString = packageJson.version;

const scattDataFileName = 'scatt_data.json';

const minimumSegmentDuration = TimeValue.msec(10);
const minimumTimelineViewDuration = TimeValue.msec(1000);
const videoAndEditorViewHeightPxMin = 100;
const videoAndEditorViewBottomMarginPxMin = 100;
const defaultVideoAndEditorViewHeightPx = 420;
const timelineTitleDefaultWidthPx = 200;

const mediaLoadingStatus = {
  notLoaded: 'notLoaded',
  loading: 'loading',
  loaded: 'loaded',
};

const defaultSegmentDataTypes = [
  LabelSegmentData,
  NoteSegmentData,
  MarkerSegmentData,
  GeneralSegmentData,
  TimeSeries1DimensionalSegmentData,
];

const defaultEditorDefinitions = [
  TimelineLabelEnumeratorDefinition,
  NoteEditorDefinition,
  TimelineSegmentInspectorDefinition,
  GeneralEditorDefinition,
];

const defaultSegmentContentDataInputDefinitions = [
  NoteSegmentContentDataInputDefinition,
  LabelSegmentContentDataInputDefinition,
  MarkerSegmentContentDataInputDefinition,
  GeneralSegmentContentDataInputDefinition,
];

class ScattConfig {
  static name = 'ScattConfig';

  constructor(
    showVideoAndEditorView,
    showTimelineView,
    videoAndEditorViewHeightPx,
    videoPlayerWidthPx,
    videoPlaybackRate,
    videoVolumeRate,
    videoMuted,
    timelineTitleWidthPx,
    isMouseMode,
    isSnapEnabled,
  ) {
    this.showVideoAndEditorView = showVideoAndEditorView;
    this.showTimelineView = showTimelineView;
    this.videoAndEditorViewHeightPx = videoAndEditorViewHeightPx;
    this.videoPlayerWidthPx = videoPlayerWidthPx;
    this.videoPlaybackRate = videoPlaybackRate;
    this.videoVolumeRate = videoVolumeRate;
    this.videoMuted = videoMuted;
    this.timelineTitleWidthPx = timelineTitleWidthPx;
    this.isMouseMode = isMouseMode;
    this.isSnapEnabled = isSnapEnabled;
  }

  static get default() {
    return new ScattConfig(
      true,
      true,
      defaultVideoAndEditorViewHeightPx,
      100,
      1,
      1,
      false,
      timelineTitleDefaultWidthPx,
      false,
      true,
    );
  }
}

async function openFileByDialog(accept = undefined) {
  return new Promise(resolve => {
      let inputElement = document.createElement('input');
      inputElement.type = 'file';
      inputElement.accept = accept;
      inputElement.onchange = (event) => { resolve(event.target.files[0]); };
      inputElement.click();
  });
}

async function fetchFileAsJson(filePath) {
  let response = await fetch(filePath);
  if (!response.ok) return undefined;
  return response.json();
}

async function fetchFileAsUint8Array(filePath) {
  let response = await fetch(filePath);
  if (!response.ok) return undefined;
  let dataLength = Number(response.headers.get('Content-Length'));
  async function writeBackOnBuffer(buffer, reader, writeOffset = 0) {
    let { done, value } = await reader.read();
    if (done) return buffer;
    buffer.set(value, writeOffset);
    writeOffset += value.length;
    return await writeBackOnBuffer(buffer, reader, writeOffset);
  }
  return writeBackOnBuffer(new Uint8Array(dataLength), response.body.getReader());
}

function getBasename(path) {
  const unixPathDelimiter = '/';
  const windowsPathDelimiter = '\\';
  let unixPathDelimiterPosition = path.lastIndexOf(unixPathDelimiter);
  let windowsPathDelimiterPosition = path.lastIndexOf(windowsPathDelimiter);
  if ((unixPathDelimiterPosition === -1) && (windowsPathDelimiterPosition === -1)) {
    return path;
  } else {
    let pathDelimiterPosition =
      (unixPathDelimiterPosition > windowsPathDelimiterPosition)?
      unixPathDelimiterPosition : windowsPathDelimiterPosition;
    return path.slice(pathDelimiterPosition + 1);
  }
}

function generateScattDataRawObj(scattData) {
  let allReferences = referencePool.getAllReferences();
  return scattData.generateRawObj(allReferences);
}

export {
  toolSemanticVersionMajor,
  toolSemanticVersionMinor,
  toolSemanticVersionPatch,
  toolSemanticVersionString,
  ScattConfig,
  ScattData,
  TimelineDataSet,
  TimelineSegmentId,
  TimelineData,
  TimelineSegmentDataBase,
  TimelineSegmentContentDataBase,
  MarkerSegmentData,
  LabelSegmentData,
  NoteSegmentData,
  GeneralSegmentData,
  PlaybackHistorySegmentData,
  TimeSeries1DimensionalSegmentData,
  EditorDefinition,
  NoteEditorDefinition,
  TimelineLabelEnumeratorDefinition,
  TimelineSegmentInspectorDefinition,
  GeneralEditorDefinition,
  TimeValue,
  AppBarButton,
  SegmentContentDataInputDefinition,
  GeneralTextField,
  GeneralTextarea,
  VDataTableWithAutoScroll,
  Reference,
  referencePool,
  utils,
  providedFunctions,
};

export default {
  name: 'App',

  components: {
    AppBar,
    TimelineListView,
    VideoAndEditorView,
    AppFooter,
    DialogManager,
    ResizableFrame,
  },

  errorCaptured(error, vm, info) {
    console.error(error, vm, info);
    this.registerFooterMessage(error.message);
    return false;
  },

  created() {
    window.addEventListener('beforeunload', this.emitBeforeUnload);
    window.addEventListener('error', this.onError);
    window.addEventListener('unhandledrejection', this.onUnhandledRejection);
    changeHistory.initialize(this.scattData);
  },

  mounted() {
    this.enableInputEventListener();
    this.appMainContainerResizeObserver.observe(this.$el);
    window.onresize = this.onResize;
  },

  destroyed() {
    window.onresize = null;
    this.disableInputEventListener();
    this.appMainContainerResizeObserver.disconnect();
    changeHistory.finalize();
    window.removeEventListener('unhandledrejection', this.onUnhandledRejection);
    window.removeEventListener('error', this.onError);
    window.removeEventListener('beforeunload', this.emitBeforeUnload);
  },

  props: {
    annotatorId:                        { type: String, default: 'scatt' },
    annotatorName:                      { type: String, default: 'Scatt' },
    editorDefinitions:                  { type: Array, default: () => defaultEditorDefinitions },
    segmentContentDataInputDefinitions: { type: Array, default: () => defaultSegmentContentDataInputDefinitions },
    segmentDataTypes:                   { type: Array, default: () => [] },
    onKeydownCallback:                  { type: Function },
    config:                             { type: ScattConfig },
    lightWaveform:                      { type: Boolean, default: true },
    webAssemblyFileLocation:            { type: String, default: 'web-assembly' },
    audioOnly:                          { type: Boolean, default: false },
    forcesBusy:                         { type: Boolean, default: false },
    scattConfigKey:                     { type: String, default: 'scattConfig' },
  },

  data() {
    return {
      timelineViewBeginMin: TimeValue.zero,
      timelineViewEndMax: TimeValue.msec(10000),
      videoDuration: TimeValue.msec(10000),
      playTime: TimeValue.zero,
      footerMessages: new FooterMessages(),
      appMainContainerResizeObserver: new ResizeObserver(this.onResize),
      selectSegmentsCallback: undefined,
      onPlayInfoUpdatedCallbackNextId: -1,
      onPlayInfoUpdatedCallbacks: {},
      scattData: ScattData.generateEmpty(),
      selectedTimelineSegmentIds: [],
      timelineSegmentIdCopySource: undefined,
      videoTitle: undefined,
      videoUrl: undefined,
      waveformDigest: undefined,
      isCurrentScattDataSameAsLastSaved: true,

      loadedConfig: undefined,

      isUndoTargetEmpty: true,
      isRedoTargetEmpty: true,

      isVideoPlaying: false,
      isLoopEnabled: false,
      loopDefinition: new LoopDefinition(TimeValue.zero, TimeValue.zero),

      timelineMarker: undefined,
      temporalMarker: undefined,

      mediaLoadingStatus: mediaLoadingStatus.notLoaded,
    };
  },

  watch: {
    scattData: {
      handler(scattData) {
        this.$emit('change', generateScattDataRawObj(scattData));
        this.normalizeSelectedTimelineSegmentIds();
      },
      deep: true,
    },

    config: {
      handler(newConfig) {
        this.loadConfig(newConfig);
      },
      deep: true,
      immediate: true,
    },

    loadedConfig: {
      handler(newLoadedConfig) {
        localStorage.setItem(this.scattConfigKey, JSON.stringify(newLoadedConfig));
      },
      deep: true,
    },
  },

  computed: {
    toolTitle() { return toolTitle; },

    isBusy() { return (this.forcesBusy || this.isMediaLoading) },

    isMediaLoading() { return (this.mediaLoadingStatus === mediaLoadingStatus.loading) },

    externalProps() {
      return {
        callback: {
          undoChange: this.undoChange,
          redoChange: this.redoChange,
          showViewFilter: this.showViewFilter,
          showHelp: this.showHelp,
          registerFooterMessage: this.registerFooterMessage,
          loadVideoDataFromLocalFile: this.loadVideoDataFromLocalFile,
          loadVideoFile: this.loadVideoFile,
          loadVideoData: this.loadVideoData,
          loadWaveformDigestDataFromLocalFile: this.loadWaveformDigestDataFromLocalFile,
          loadWaveformDigestDataFromVideoData: this.loadWaveformDigestDataFromVideoData,
          loadWaveformDigestFile: this.loadWaveformDigestFile,
          loadWaveformDigestData: this.loadWaveformDigestData,
          loadScattDataFromLocalFile: this.loadScattDataFromLocalFile,
          loadScattFile: this.loadScattFile,
          loadScattData: this.loadScattData,
          downloadScattData: this.downloadScattData,
          saveScattData: this.saveScattData,
          createNewScattData: this.createNewScattData,
          getCurrentScattData: this.getCurrentScattData,
          openDialog: this.openDialog,
        },
        state: {
          isCurrentScattDataSameAsLastSaved: this.isCurrentScattDataSameAsLastSaved,
          isUndoTargetEmpty: this.isUndoTargetEmpty,
          isRedoTargetEmpty: this.isRedoTargetEmpty,
        },
      };
    },

    videoTimeRange() {
      return new VideoTimeRange(
        this.timelineViewBeginMin.floorTo(TimeValue.zero),
        this.timelineViewEndMax.ceilTo(this.videoDuration),
      );
    },

    minimumSegmentDuration() { return minimumSegmentDuration; },

    visibleTimelineDataSet() {
      let visibleTimelineDataSet = new TimelineDataSet();
      for (let [ timelineDataId, timelineData ] of Object.entries(this.scattData.timelineDataSet)) {
        if (timelineData.hidden) continue;
        visibleTimelineDataSet[timelineDataId] = timelineData;
      }
      return visibleTimelineDataSet;
    },

    visibleTimelineDataIdxToId() {
      return Object.keys(this.visibleTimelineDataSet)
        .sort((timelineDataIdA, timelineDataIdB) => {
          let timelineDataA = this.visibleTimelineDataSet[timelineDataIdA];
          let timelineDataB = this.visibleTimelineDataSet[timelineDataIdB];
          return (timelineDataA.timelineDataIdx - timelineDataB.timelineDataIdx);
        });
    },

    sortedVisibleSegmentIdxToIdMaps() {
      let sortedVisibleSegmentIdxToIdMaps = new Map();
      for (let [ timelineDataId, timelineData ] of Object.entries(this.visibleTimelineDataSet)) {
        let sortedVisibleSegmentIdxToIdMap = Object.keys(timelineData.segments).sort(timelineData.getBeginTimeAscendingOrderIdFunction());
        sortedVisibleSegmentIdxToIdMaps.set(timelineDataId, sortedVisibleSegmentIdxToIdMap);
      }
      return sortedVisibleSegmentIdxToIdMaps;
    },

    visibleSegmentIdToSortedIdxMaps() {
      let visibleSegmentIdToSortedIdxMaps = new Map();
      for (let [ timelineDataId, sortedVisibleSegmentIdxToIdMap ] of this.sortedVisibleSegmentIdxToIdMaps) {
        let visibleSegmentIdToSortedIdxMap = new Map();
        sortedVisibleSegmentIdxToIdMap.forEach((segmentId, sortedSegmentIdx) => {
          visibleSegmentIdToSortedIdxMap.set(segmentId, sortedSegmentIdx);
        })
        visibleSegmentIdToSortedIdxMaps.set(timelineDataId, visibleSegmentIdToSortedIdxMap);
      }
      return visibleSegmentIdToSortedIdxMaps;
    },

    timelineDataIdOfNoteByCurrentUser() {
      return this.scattData.timelineDataSet.ids.find((timelineDataId) => {
        let timelineData = this.scattData.timelineDataSet[timelineDataId];
        if (timelineData.segmentDataType === NoteSegmentData) {
          return timelineData.authorId === this.annotatorId;
        }
      });
    },

    timelineDataIdOfPlaybackHistoryByCurrentUser() {
      return this.scattData.timelineDataSet.ids.find((timelineDataId) => {
        let timelineData = this.scattData.timelineDataSet[timelineDataId];
        if (timelineData.segmentDataType === PlaybackHistorySegmentData) {
          return timelineData.authorId === this.annotatorId;
        }
      });
    },

    toolSemanticVersionString() { return toolSemanticVersionString; },

    mergedEditorDefinitions() {
      let editorDefinitionSet = new Set(this.editorDefinitions);
      for (let editorDefinition of defaultEditorDefinitions) {
        editorDefinitionSet.add(editorDefinition);
      }
      return [ ...editorDefinitionSet ];
    },

    mergedSegmentContentDataInputDefinitions() {
      let mergedSegmentContentDataInputDefinitionSet = new Set(this.segmentContentDataInputDefinitions);
      for (let segmentContentDataInputDefinition of defaultSegmentContentDataInputDefinitions) {
        mergedSegmentContentDataInputDefinitionSet.add(segmentContentDataInputDefinition);
      }
      return [ ...mergedSegmentContentDataInputDefinitionSet ];
    },

    mergedSegmentDataTypes() {
      let mergedSegmentDataTypeSet = new Set(this.segmentDataTypes);
      for (let segmentDataType of defaultSegmentDataTypes) {
        mergedSegmentDataTypeSet.add(segmentDataType);
      }
      return [ ...mergedSegmentDataTypeSet ];
    },

    isAnySelectedTimelineSegmentImmutable() {
      for (let timelineSegmentId of this.selectedTimelineSegmentIds) {
        if (this.scattData.timelineDataSet.isTimelineSegmentImmutable(timelineSegmentId)) return true;
      }
      return false;
    },

    minimumBeginSegmentInSelectedTimelineSegments() {
      if (this.selectedTimelineSegmentIds.length === 0) return undefined;
      let initialSelectedTimelineSegmentId = this.selectedTimelineSegmentIds[0];
      let minimumBeginSegment = this.scattData.timelineDataSet.getSegment(initialSelectedTimelineSegmentId);
      for(let selectedTimelineSegmentId of this.selectedTimelineSegmentIds) {
        let segment = this.scattData.timelineDataSet.getSegment(selectedTimelineSegmentId);
        if (segment.begin === null) {
          return segment;
        }
        if (segment.begin.isLessThan(minimumBeginSegment.begin)) {
          minimumBeginSegment = segment;
        }
      }
      return minimumBeginSegment;
    },

    maximumEndSegmentInSelectedTimelineSegments() {
      if (this.selectedTimelineSegmentIds.length === 0) return undefined;
      let initialSelectedTimelineSegmentId = this.selectedTimelineSegmentIds[0];
      let maximumEndSegment = this.scattData.timelineDataSet.getSegment(initialSelectedTimelineSegmentId);
      for(let selectedTimelineSegmentId of this.selectedTimelineSegmentIds) {
        let segment = this.scattData.timelineDataSet.getSegment(selectedTimelineSegmentId);
        if (segment.end === null) {
          return segment;
        }
        if (segment.end.isGreaterThan(maximumEndSegment.end)) {
          maximumEndSegment = segment;
        }
      }
      return maximumEndSegment;
    },

    noteTimelineDataNameByCurrentUser() {
      return `Notes - ${this.annotatorName}`;
    },

    playbackHistoryTimelineDataNameByCurrentUser() {
      return `PlaybackHistory - ${this.annotatorName}`;
    },

    videoAndEditorViewHeightPx: {
      get() {
        if (!this.loadedConfig.showVideoAndEditorView) return 0;
        if (!this.loadedConfig.showTimelineView) return undefined;
        return this.loadedConfig.videoAndEditorViewHeightPx;
      },
      set(videoAndEditorViewHeightPx) { this.setVideoAndEditorViewHeightPx(videoAndEditorViewHeightPx) },
    }
  },

  provide() {
    let mergedProvidedFunctions = {
      generateNewTimelineDataFromExistingTimelines: this.generateNewTimelineDataFromExistingTimelines,
      importTimelineDataFromExistingTimelines: this.importTimelineDataFromExistingTimelines,
      moveTimelineSegments: this.moveTimelineSegments,
      incrementVisibleTimelineDataIdx: this.incrementVisibleTimelineDataIdx,
      filterTimelineData: this.filterTimelineData,
      selectNextTimelineSegment: this.selectNextTimelineSegment,
      selectPreviousTimelineSegment: this.selectPreviousTimelineSegment,
      showDurationEditor: this.showDurationEditor,
      showViewFilter: this.showViewFilter,
      showHelp: this.showHelp,
      showVersion: this.showVersion,
      showFooterMessageLogs: this.showFooterMessageLogs,
      undoChange: this.undoChange,
      redoChange: this.redoChange,
    };
    for (let providedFunctionName of providedFunctions) {
      mergedProvidedFunctions[providedFunctionName] = this[providedFunctionName];
    }
    return mergedProvidedFunctions;
  },

  methods: {
    loadConfig(newConfig) {
      if (newConfig === undefined) {
        try {
          newConfig = JSON.parse(localStorage.getItem(this.scattConfigKey));
        } catch (error) {
          if (!(error instanceof SyntaxError)) throw error;
        }
        if (newConfig === null) newConfig = ScattConfig.default;
      }

      let normalizedConfig = {};
      let validConfigKeys = Object.keys(ScattConfig.default);
      let newConfigKeys = Object.keys(newConfig);
      for (let validConfigKey of validConfigKeys) {
        if (newConfigKeys.includes(validConfigKey)) {
          normalizedConfig[validConfigKey] = newConfig[validConfigKey];
        } else {
          normalizedConfig[validConfigKey] = ScattConfig.default[validConfigKey];
        }
      }
      this.loadedConfig = normalizedConfig;
    },

    onMetaDataLoaded(metaData) {
      this.timelineViewBeginMin.assign(TimeValue.zero);
      this.timelineViewEndMax.assign(metaData.duration);
      this.videoDuration.assign(metaData.duration);
      this.loopDefinition.assign({ begin: this.timelineViewBeginMin, end: this.timelineViewEndMax });
      this.$emit('meta-data-loaded', metaData);
    },

    onPlayInfoUpdated(playInfo) {
      this.playTime = playInfo.playTime;
      this.isVideoPlaying = playInfo.isPlaying;
      for (let callback of Object.values(this.onPlayInfoUpdatedCallbacks)) {
        callback(playInfo);
      }
    },

    onResize() {
      // WORKAROUND: requestAnimationFrame to supress error "ResizeObserver loop limit exceeded"
      window.requestAnimationFrame(() => {
        if (this.loadedConfig.showTimelineView) {
          this.setVideoAndEditorViewHeightPx(this.videoAndEditorViewHeightPx);
        }
      });
    },

    onMousemove(event) {
      if (this.isBusy) return;
      if (this.loadedConfig.showTimelineView) {
        this.$refs.timelineListView.onMousemove(event);
      }
      if (this.loadedConfig.showVideoAndEditorView) {
        this.$refs.videoAndEditorViewResizableFrame.onMousemove(event);
        this.$refs.videoAndEditorView.onMousemove(event);
      }
    },

    onMouseup(event) {
      if (this.isBusy) return;
      if (this.loadedConfig.showTimelineView) {
        this.$refs.timelineListView.onMouseup(event);
      }
      if (this.loadedConfig.showVideoAndEditorView) {
        this.$refs.videoAndEditorViewResizableFrame.onMouseup(event);
        this.$refs.videoAndEditorView.onMouseup();
      }
    },

    onScroll(event) {
      if (this.isBusy) return;
      if (this.loadedConfig.showTimelineView) {
        if (this.$refs.timelineListView.onScroll(event)) {
          event.preventDefault();
        }
      }
    },

    async onKeydown(event) {
      if (this.isBusy) return;
      {
        if (event.code === 'Escape') {
          if (document.activeElement !== document.body) {
            document.activeElement.blur();
            event.preventDefault();
            return;
          }
        }
        let withCtrl = event.ctrlKey;
        let withShift = event.shiftKey;
        if (document.activeElement !== document.body) return;
        if (this.onKeydownCallback) {
          if (await this.onKeydownCallback(event, this.externalProps)) {
            event.preventDefault();
            return;
          }
        }
        if (this.loadedConfig.showVideoAndEditorView) {
          if (this.$refs.videoAndEditorView.onKeydown(event)) {
            event.preventDefault();
            return;
          }
        }
        switch (event.code) {
        case 'Space':
          await this.$refs.videoAndEditorView.togglePlayAndPause();
          return true;
        case 'KeyV':
          if (!onKeydownOnTimelineListView.call(this, event)) {
            await this.showViewFilter();
          }
          break;
        case 'KeyH':
          this.showHelp();
          break;
        case 'KeyZ':
          if (withCtrl) {
            if (!withShift) {
              this.undoChange();
            } else {
              this.redoChange();
            }
          }
          break;
        case 'Digit1':
          this.loadedConfig.videoPlaybackRate = 1;
          return;
        case 'Digit2':
          this.loadedConfig.videoPlaybackRate = 2;
          return;
        case 'Digit3':
          this.loadedConfig.videoPlaybackRate = 3;
          return;
        case 'Digit4':
          this.loadedConfig.videoPlaybackRate = 4;
          return;
        default:
          if (!onKeydownOnTimelineListView.call(this, event)) return;
          break;
        }
        event.preventDefault();
      }

      function onKeydownOnTimelineListView(event) {
        if (!this.loadedConfig.showTimelineView) return false;
        return this.$refs.timelineListView.onKeydown(event);
      }
    },

    onError(error) {
      console.error(error);
      error.preventDefault();
      this.registerFooterMessage(error.message);
    },

    onUnhandledRejection(promiseRejection) {
      console.error(promiseRejection);
      promiseRejection.preventDefault();
      let errorMessage = undefined;
      if (promiseRejection.reason instanceof Error) {
        errorMessage = promiseRejection.reason.message;
      } else {
        errorMessage = promiseRejection.reason;
      }
      this.registerFooterMessage(errorMessage);
    },

    normalizeSelectedTimelineSegmentIds() {
      let validTimelineSegmentIds = [];
      for (let selectedTimelineSegmentId of this.selectedTimelineSegmentIds) {
        let selectedTimelineData = this.scattData.timelineDataSet[selectedTimelineSegmentId.timelineDataId];
        if (!selectedTimelineData) continue;
        if (selectedTimelineData.hidden) continue;
        if (this.scattData.timelineDataSet.getSegment(selectedTimelineSegmentId)) {
          validTimelineSegmentIds.push(selectedTimelineSegmentId);
        }
      }
      this.selectedTimelineSegmentIds = validTimelineSegmentIds;
    },

    setLoopFullDuration() {
      this.loopDefinition.assign({ begin: this.timelineViewBeginMin, end: this.timelineViewEndMax });
      this.isLoopEnabled = true;
    },

    setLoopByMarker() {
      if (this.timelineMarker !== undefined) {
        this.loopDefinition.assign({
          begin: (this.timelineMarker.begin === null)? this.timelineViewBeginMin : this.timelineMarker.begin,
          end: (this.timelineMarker.end === null)? this.timelineViewEndMax : this.timelineMarker.end,
        });
      }
    },

    setMarker(begin, end) {
      if (this.timelineMarker === undefined) {
        this.timelineMarker = new TimelineMarker(begin, end);
      } else {
        this.timelineMarker.assign({ begin, end });
      }
    },

    clearMarker() {
      this.timelineMarker = undefined;
    },

    generateNoteTimelineDataByCurrentUser() {
      let newTimelineDataId = this.scattData.timelineDataSet.generateNewId();
      let newTimelineData = this.scattData.timelineDataSet.generateEmptyTimelineData(
        this.noteTimelineDataNameByCurrentUser,
        NoteSegmentData,
        this.annotatorId,
        this.annotatorName,
        { hidden: true },
      );
      this.$set(this.scattData.timelineDataSet, newTimelineDataId, newTimelineData);
      this.registerChange('generating note timeline', 'Note timeline generated.');
    },

    generatePlaybackHistoryTimelineDataByCurrentUser() {
      let newTimelineDataId = this.scattData.timelineDataSet.generateNewId();
      let newTimelineData = this.scattData.timelineDataSet.generateEmptyTimelineData(
        this.playbackHistoryTimelineDataNameByCurrentUser,
        PlaybackHistorySegmentData,
        this.annotatorId,
        this.annotatorName,
        { readonly: true, hidden: true },
      );
      this.$set(this.scattData.timelineDataSet, newTimelineDataId, newTimelineData);
    },

    async generateNewTimelineData(timelineSegmentDataType, newDefaultTimelineName, isReadonly) {
      return await this.openDialog(
        'general-text-field-editor',
        {
          title: 'Timeline data name',
          text: newDefaultTimelineName,
          okCallback: (newTimelineName) => {
            let newTimelineDataId = this.generateNewTimelineDataCore(timelineSegmentDataType, newTimelineName, isReadonly);
            this.registerChange(
              `generating new ${timelineSegmentDataType} timeline`,
              `New ${timelineSegmentDataType} timeline generated.`,
            );
            return newTimelineDataId;
          },
        },
      );
    },

    generateNewHiddenTimelineData(timelineSegmentDataType, newTimelineName, isReadonly) {
      let newTimelineDataId = this.generateNewTimelineDataCore(timelineSegmentDataType, newTimelineName, isReadonly);
      this.scattData.timelineDataSet[newTimelineDataId].hidden = true;
      this.registerChange(
        `generating new ${timelineSegmentDataType} timeline`,
        `New ${timelineSegmentDataType} timeline generated.`,
      );
      return newTimelineDataId;
    },

    async generateNewTimelineDataFromExistingTimelines(isReadonly) {
      return await this.openDialog(
        'timeline-data-selector',
        {
          timelineDataSet: this.scattData.timelineDataSet,
          okCallback: (timelineSegmentDataType, selectedTimelineDataIds) => {
            this.atomicChanges(
              'generating timeline data from existing timelines',
              'Timeline data generated from existing timelines.',
              () => {
                let newTimelineName = `New ${timelineSegmentDataType} Timeline`;
                let newTimelineDataId = this.generateNewTimelineDataCore(timelineSegmentDataType, newTimelineName, isReadonly);
                let newTimelineData = this.scattData.timelineDataSet[newTimelineDataId];
                for (let selectedTimelineDataId of selectedTimelineDataIds) {
                  let selectedTimelineData = this.scattData.timelineDataSet[selectedTimelineDataId];
                  for (let segment of Object.values(selectedTimelineData.segments)) {
                    let newSegmentId = newTimelineData.generateNewId();
                    let newTimelineSegmentId = new TimelineSegmentId(newTimelineDataId, newSegmentId);
                    this.setSegmentCore(newTimelineSegmentId, timelineSegmentDataType, segment, false);
                  }
                }
                this.registerChangeCore();
              },
            );
          },
        },
      );
    },

    async importTimelineDataFromExistingTimelines(timelineDataId) {
      let timelineDataSegmentType = this.scattData.timelineDataSet[timelineDataId].segmentDataType;
      return await this.openDialog(
        'timeline-data-selector',
        {
          disabledTimelineDataIds: [ timelineDataId ],
          segmentDataType: timelineDataSegmentType,
          timelineDataSet: this.scattData.timelineDataSet,
          okCallback: (timelineSegmentDataType, selectedTimelineDataIds) => {
            this.atomicChanges(
              'importing timeline data from existing timelines',
              'Timeline data imported from existing timelines.',
              () => {
                let targetTimelineData = this.scattData.timelineDataSet[timelineDataId];
                for (let selectedTimelineDataId of selectedTimelineDataIds) {
                  let selectedTimelineData = this.scattData.timelineDataSet[selectedTimelineDataId];
                  for (let segment of Object.values(selectedTimelineData.segments)) {
                    let newSegmentId = targetTimelineData.generateNewId();
                    let newTimelineSegmentId = new TimelineSegmentId(timelineDataId, newSegmentId);
                    this.setSegmentCore(newTimelineSegmentId, timelineSegmentDataType, segment, false);
                  }
                }
                this.registerChangeCore();
              },
            );
          },
        },
      );
    },

    async moveTimelineSegments(selectedTimelineSegmentIds, timelineSegmentDataType) {
      return await this.openDialog(
        'timeline-data-selector',
        {
          multiple: false,
          timelineDataSet: this.scattData.timelineDataSet,
          segmentDataType: timelineSegmentDataType,
          okCallback: (timelineSegmentDataType, targetTimelineDataId) => {
            this.atomicChanges(
              'moving timeline segments',
              'Timeline segments are moved.',
              () => {
                let targetTimelineData = this.scattData.timelineDataSet[targetTimelineDataId];
                for (let selectedTimelineSegmentId of selectedTimelineSegmentIds) {
                  let selectedTimelineDataId = selectedTimelineSegmentId.timelineDataId;
                  let selectedSegmentId = selectedTimelineSegmentId.segmentId;
                  let selectedTimelineData = this.scattData.timelineDataSet[selectedTimelineDataId];
                  let selectedTimelineSegment = selectedTimelineData.segments[selectedSegmentId];
                  delete selectedTimelineData.segments[selectedSegmentId];
                  let newSegmentId = targetTimelineData.generateNewId();
                  let newTimelineSegmentId = new TimelineSegmentId(targetTimelineDataId, newSegmentId);
                  this.setSegmentCore(newTimelineSegmentId, timelineSegmentDataType, selectedTimelineSegment, false);
                }
                this.registerChangeCore();
              },
            );
          },
        },
      );
    },

    duplicateTimelineData(timelineDataId) {
      let newTimelineDataId = this.scattData.timelineDataSet.generateNewId();
      let newTimelineDataIdx = this.scattData.timelineDataSet.nextIdx;
      let timelineData = this.scattData.timelineDataSet[timelineDataId];
      let timelineDataIdx = timelineData.timelineDataIdx;
      let duplicatedTimelineData = timelineData.clone(false);
      duplicatedTimelineData.timelineDataIdx = newTimelineDataIdx;
      this.$set(this.scattData.timelineDataSet, newTimelineDataId, duplicatedTimelineData);
      this.scattData.timelineDataSet.moveTimelineDataIdxTo(newTimelineDataIdx, timelineDataIdx + 1);
      this.registerChange('duplicating timeline', 'Timeline duplicated.');
    },

    removeTimelineData(timelineDataId) {
      let referenceIdsToDelete = [];
      for (let [ referenceId, reference ] of Object.entries(referencePool.getAllReferences())) {
        if ((reference.referFrom.timelineDataId === timelineDataId) || (reference.referTo.timelineDataId === timelineDataId)) {
          referenceIdsToDelete.push(referenceId);
        }
      }
      this.unregisterReferences(...referenceIdsToDelete);
      this.$delete(this.scattData.timelineDataSet, timelineDataId);
      this.scattData.timelineDataSet.normalizeTimelineDataIdcs();
      this.registerChange('removing timeline', 'Timeline removed.');
    },

    async renameTimelineData(timelineDataId) {
      let timelineData = this.scattData.timelineDataSet[timelineDataId];
      return await this.openDialog(
        'general-text-field-editor',
        {
          title: 'Timeline data name',
          text: timelineData.name,
          okCallback: (res) => {
            timelineData.name = res;
            this.registerChange('renaming timeline', 'Timeline renamed.');
          },
        },
      );
    },

    incrementVisibleTimelineDataIdx(visibleTimelineDataIdx, inverted) {
      let numVisibleTimelines = Object.keys(this.visibleTimelineDataSet).length;
      let visibleTimelineDataIdxL = (inverted)? (visibleTimelineDataIdx - 1) : visibleTimelineDataIdx;
      if (visibleTimelineDataIdxL < 0) return;
      let visibleTimelineDataIdxH = (inverted)? visibleTimelineDataIdx : (visibleTimelineDataIdx + 1);
      if (visibleTimelineDataIdxH >= numVisibleTimelines) return;
      let timelineDataIdL = this.visibleTimelineDataIdxToId[visibleTimelineDataIdxL];
      let timelineDataIdH = this.visibleTimelineDataIdxToId[visibleTimelineDataIdxH];
      let timelineDataL = this.scattData.timelineDataSet[timelineDataIdL];
      let timelineDataH = this.scattData.timelineDataSet[timelineDataIdH];
      let timelineDataIdxL = timelineDataL.timelineDataIdx;
      let timelineDataIdxH = timelineDataH.timelineDataIdx;
      this.scattData.timelineDataSet.moveTimelineDataIdxTo(timelineDataIdxL, timelineDataIdxH);
      this.registerChange('swapping timelines', 'Timeline swapped.');
    },

    async filterTimelineData() {
      return await this.openDialog(
        'timeline-data-filter',
        {
          timelineDataSet: this.scattData.timelineDataSet,
          okCallback: (isTimelineDataHiddenSet) => {
            let isHiddennessUpdated = false;
            for (let [ timelineDataId, isTimelineDataHidden ] of Object.entries(isTimelineDataHiddenSet)) {
              let timelineData = this.scattData.timelineDataSet[timelineDataId];
              if (timelineData.hidden !== isTimelineDataHidden) {
                timelineData.hidden = isTimelineDataHidden;
                isHiddennessUpdated = true;
              }
            }
            if (isHiddennessUpdated) {
              this.registerChange('updating timeline filter', 'Timeline filter updated.');
            }
          },
        },
      );
    },

    hideTimelineData(timelineDataId) {
      let timelineData = this.scattData.timelineDataSet[timelineDataId];
      if (!timelineData.hidden) {
        timelineData.hidden = true;
        this.registerChange('hide timeline', 'Hid timeline.');
      }
    },

    unhideTimelineData(timelineDataId) {
      let timelineData = this.scattData.timelineDataSet[timelineDataId];
      if (timelineData.hidden) {
        timelineData.hidden = false;
        this.registerChange('unhide timeline', 'Unhid timeline.');
      }
    },

    lockTimeline(timelineDataId) {
      let timelineData = this.scattData.timelineDataSet[timelineDataId];
      if (!timelineData.locked) {
        timelineData.locked = true;
        this.registerChange('locking timeline', 'Timeline locked');
      }
    },

    unlockTimeline(timelineDataId) {
      let timelineData = this.scattData.timelineDataSet[timelineDataId];
      if (timelineData.locked) {
        timelineData.locked = false;
        this.registerChange('unlocking timeline', 'Timeline unlocked.');
      }
    },

    selectTimelineSegments(...timelineSegmentIds) {
      for (let timelineSegmentId of timelineSegmentIds) {
        if (!this.isTimelineSegmentIdSelected(timelineSegmentId)) {
          this.selectedTimelineSegmentIds.push(timelineSegmentId);
        }
      }
    },

    selectTimelineSegmentAndSeek(timelineSegmentId) {
      let timelineSegment = this.scattData.timelineDataSet.getSegment(timelineSegmentId);
      this.seek(timelineSegment.begin);
      if (this.loadedConfig.showTimelineView) {
        this.$refs.timelineListView.selectTimelineSegment(timelineSegmentId);
      }
    },

    selectNextTimelineSegment() {
      if (this.loadedConfig.showTimelineView) {
        this.$refs.timelineListView.selectNextSegment();
      }
    },

    selectPreviousTimelineSegment() {
      if (this.loadedConfig.showTimelineView) {
        this.$refs.timelineListView.selectPreviousSegment();
      }
    },

    isTimelineSegmentIdSelected(timelineSegmentId) {
      for (let selectedTimelineSegmentId of this.selectedTimelineSegmentIds) {
        if (selectedTimelineSegmentId.isSameAs(timelineSegmentId)) return true;
      }
      return false;
    },

    clearSelectedTimelineSegments() {
      this.selectedTimelineSegmentIds = [];
    },

    async invokeActionOnSelectedTimelineSegments(action) {
      {
        let numSelectedTimelineSegments = this.selectedTimelineSegmentIds.length;
        if (numSelectedTimelineSegments === 0) return;
        let onlyOneSelectedTimelineSegmentId = (numSelectedTimelineSegments === 1)? this.selectedTimelineSegmentIds[0] : undefined;
        switch(action) {
        case 'general':
          if (this.selectSegmentsCallback !== undefined) {
            invokeSelectSegmentsCallback.call(this, this.selectedTimelineSegmentIds);
          } else {
            this.seek(this.minimumBeginSegmentInSelectedTimelineSegments.begin);
          }
          break;
        case 'seek':
          this.seek(this.minimumBeginSegmentInSelectedTimelineSegments.begin);
          break;
        case 'editSegment':
          if (onlyOneSelectedTimelineSegmentId) {
            await editSegment.call(this, onlyOneSelectedTimelineSegmentId);
          }
          break;
        case 'deleteSegment':
          this.deleteSegments(this.selectedTimelineSegmentIds);
          break;
        case 'clearSegment':
          this.clearSegments(this.selectedTimelineSegmentIds);
          break;
        case 'mergeSegment':
          this.mergeSegments(this.selectedTimelineSegmentIds);
          break;
        case 'addNote':
          await addNotes.call(this, this.selectedTimelineSegmentIds);
          break;
        case 'setLoop':
          setLoopBySelectedSegments.call(this, this.minimumBeginSegmentInSelectedTimelineSegments, this.maximumEndSegmentInSelectedTimelineSegments);
          break;
        case 'setMarker':
          setMarkerBySelectedSegments.call(this, this.minimumBeginSegmentInSelectedTimelineSegments, this.maximumEndSegmentInSelectedTimelineSegments);
          break;
        case 'setExclusiveMarker':
          if (numSelectedTimelineSegments > 1) {
            setExclusiveMarkerBySelectedSegments.call(this, this.minimumBeginSegmentInSelectedTimelineSegments, this.maximumEndSegmentInSelectedTimelineSegments);
          }
          break;
        case 'lock':
          setSelectedSegmentsLocked.call(this, this.selectedTimelineSegmentIds, true);
          break;
        case 'unlock':
          setSelectedSegmentsLocked.call(this, this.selectedTimelineSegmentIds, false);
          break;
        case 'setCopySource':
          if (onlyOneSelectedTimelineSegmentId) {
            setCopySourceBySelectedSegments.call(this, onlyOneSelectedTimelineSegmentId);
          }
          break;
        }
      }

      function invokeSelectSegmentsCallback(timelineSegmentIds) {
        this.selectSegmentsCallback(timelineSegmentIds);
      }

      async function editSegment(timelineSegmentId) {
        let timelineData = this.scattData.timelineDataSet[timelineSegmentId.timelineDataId];
        let segment = this.scattData.timelineDataSet.getSegment(timelineSegmentId);
        let isSegmentImmutable = timelineData.isSegmentImmutable(timelineSegmentId.segmentId);
        let visibleSegmentIdToSortedIdxMap = this.visibleSegmentIdToSortedIdxMaps.get(timelineSegmentId.timelineDataId);
        let sortedSegmentIdx = visibleSegmentIdToSortedIdxMap.get(timelineSegmentId.segmentId);
        let numSegments = timelineData.numSegments;
        let reopenPreviousSegmentEnabled = (sortedSegmentIdx > 0);
        let reopenNextSegmentEnabled = (sortedSegmentIdx < (numSegments - 1));
        return await this.openDialog(
          'timeline-segment-editor',
          {
            timelineDataSet: this.scattData.timelineDataSet,
            minimumSegmentDurationMsec: minimumSegmentDuration.msec,
            targetSegment: segment,
            targetTimelineSegmentId: timelineSegmentId,
            isImmutable: isSegmentImmutable,
            reopenPreviousSegmentEnabled: reopenPreviousSegmentEnabled,
            reopenNextSegmentEnabled: reopenNextSegmentEnabled,
            segmentContentDataInputDefinitions: this.mergedSegmentContentDataInputDefinitions,
            okCallback: (segmentEdited) => {
              if (this.setSegmentCore(timelineSegmentId, timelineData.segmentDataType, segmentEdited, false)) {
                this.registerChange('editing segment', 'Segment edited.');
              }
            },
          },
        );
      }

      async function addNotes(timelineSegmentIdsReferTo) {
        if (!this.timelineDataIdOfNoteByCurrentUser) {
          this.generateNoteTimelineDataByCurrentUser();
        }
        let lastSeletedTimelineSegmentId = timelineSegmentIdsReferTo[timelineSegmentIdsReferTo.length - 1];
        let representativeSelectedTimelineSegment = this.scattData.timelineDataSet.getSegment(lastSeletedTimelineSegmentId);
        let noteTimelineData = this.scattData.timelineDataSet[this.timelineDataIdOfNoteByCurrentUser];
        let noteTimelineDataId = this.timelineDataIdOfNoteByCurrentUser;
        let newNoteSegmentId = noteTimelineData.generateNewId();
        let noteTimelineSegmentId = new TimelineSegmentId(noteTimelineDataId, newNoteSegmentId);
        let emptyNoteSegment = NoteSegmentData.generateEmpty(representativeSelectedTimelineSegment.begin, representativeSelectedTimelineSegment.end);

        return await this.openDialog(
          'timeline-segment-editor',
          {
            timelineDataSet: this.scattData.timelineDataSet,
            minimumSegmentDurationMsec: minimumSegmentDuration.msec,
            targetSegment: emptyNoteSegment,
            targetTimelineSegmentId: newNoteSegmentId,
            isImmutable: false,
            reopenPreviousSegmentEnabled: false,
            reopenNextSegmentEnabled: false,
            segmentContentDataInputDefinitions: this.mergedSegmentContentDataInputDefinitions,
            okCallback: (segment) => {
              this.atomicChanges(
                'adding note',
                'Note added.',
                () => {
                  if (this.setSegmentCore(noteTimelineSegmentId, NoteSegmentData, segment, false)) {
                    this.registerChangeCore();
                    let references = timelineSegmentIdsReferTo
                      .map(timelineSegmentId => new Reference('Note', noteTimelineSegmentId, timelineSegmentId));
                    this.registerReferences(...references);
                  }
                },
              );
            },
          },
        );
      }

      function setLoopBySelectedSegments(minimumBeginSegmentInSelectedTimelineSegments, maximumEndSegmentInSelectedTimelineSegments) {
        this.loopDefinition.assign({
          begin: (minimumBeginSegmentInSelectedTimelineSegments.begin === null)? this.timelineViewBeginMin : minimumBeginSegmentInSelectedTimelineSegments.begin,
          end: (maximumEndSegmentInSelectedTimelineSegments.end === null)? this.timelineViewEndMax : maximumEndSegmentInSelectedTimelineSegments.end,
        });
        this.isLoopEnabled = true;
      }

      function setMarkerBySelectedSegments(minimumBeginSegmentInSelectedTimelineSegments, maximumEndSegmentInSelectedTimelineSegments) {
        this.setMarker(minimumBeginSegmentInSelectedTimelineSegments.begin, maximumEndSegmentInSelectedTimelineSegments.end);
      }

      function setExclusiveMarkerBySelectedSegments(minimumBeginSegmentInSelectedTimelineSegments, maximumEndSegmentInSelectedTimelineSegments) {
        if (maximumEndSegmentInSelectedTimelineSegments.begin.isLessThan(minimumBeginSegmentInSelectedTimelineSegments.end)) {
          this.setMarker(maximumEndSegmentInSelectedTimelineSegments.begin, minimumBeginSegmentInSelectedTimelineSegments.end);
        } else {
          this.setMarker(minimumBeginSegmentInSelectedTimelineSegments.end, maximumEndSegmentInSelectedTimelineSegments.begin);
        }
      }

      function setSelectedSegmentsLocked(selectedTimelineSegmentIds, isLocked) {
        let isLockedSet = false;
        for(let selectedTimelineSegmentId of selectedTimelineSegmentIds) {
          let segment = this.scattData.timelineDataSet.getSegment(selectedTimelineSegmentId);
          if (segment.locked !== isLocked) {
            segment.locked = isLocked;
            isLockedSet = true;
          }
        }
        if (isLockedSet) {
          this.registerChange(
            `${(isLocked)? 'locking' : 'unlocking'} segments`,
            `Segments ${((isLocked)? 'locked' : 'unlocked')}`,
          );
        }
      }

      function setCopySourceBySelectedSegments(selectedTimelineDataId) {
        this.timelineSegmentIdCopySource = selectedTimelineDataId;
      }
    },

    getTimelineDataImmutableReason(timelineData, ignoresReadonly) {
      if (!ignoresReadonly && timelineData.readonly) {
        return 'Timeline is readonly';
      }
      if (timelineData.locked) {
        return 'Timeline is locked';
      }
      return undefined;
    },

    getSegmentUnsettableReason(timelineSegmentId, segmentDataType, segment, ignoresReadonly) {
      let timelineData = this.scattData.timelineDataSet[timelineSegmentId.timelineDataId];
      let timelineImmutableReason = this.getTimelineDataImmutableReason(timelineData, ignoresReadonly);
      if (timelineImmutableReason !== undefined) return timelineImmutableReason;
      if (timelineData.segmentDataType !== segmentDataType) {
        return 'Segment data type does not match.';
      }
      let segmentAlreadyExisting = this.scattData.timelineDataSet.getSegment(timelineSegmentId);
      if (segmentAlreadyExisting) {
        if (segmentAlreadyExisting.locked) {
          return 'Segment is locked.';
        }
      }
      if (segment.begin !== null) {
        if (segment.begin.isLessThan(TimeValue.zero)) return 'Segment begin time must be more than or equal to 0';
      }
      if (segment.end !== null) {
        if (segment.end.isLessThan(TimeValue.zero)) return 'Segment end time must be more than or equal to 0';
      }
      if ((segment.begin !== null) && (segment.end !== null)) {
        if (segment.end.isLessThanOrEqualTo(segment.begin)) {
          return 'Segment end time must be more than begin time.';
        }
        let segmentDuration = segment.end.getOffsetFrom(segment.begin);
        if (segmentDuration.isLessThan(minimumSegmentDuration)) {
          return 'Segment is too short.';
        }
      }
      return undefined;
    },

    setSegmentCore(timelineSegmentId, segmentDataType, segment, ignoresReadonly) {
      if (this.getSegmentUnsettableReason(timelineSegmentId, segmentDataType, segment, ignoresReadonly) !== undefined) return false;
      let segmentAlreadyExisting = this.scattData.timelineDataSet.getSegment(timelineSegmentId);
      if (segmentAlreadyExisting) {
        if (segmentAlreadyExisting.isSameAs(segment)) return false;
      }
      let timelineDataId = timelineSegmentId.timelineDataId;
      let segmentId = timelineSegmentId.segmentId;
      this.$set(this.scattData.timelineDataSet[timelineDataId].segments, segmentId, segment);
      return true;
    },

    setSegment(
      timelineSegmentId, segmentDataType, segment,
      footerMessage = 'Segment is set.',
      changeSummary = 'setting segment',
      { undoCallback = undefined, redoCallback = undefined, ignoresReadonly = false } = {})
    {
      let unsettableReason = this.getSegmentUnsettableReason(timelineSegmentId, segmentDataType, segment, ignoresReadonly);
      if (unsettableReason !== undefined) {
        this.registerFooterMessage(unsettableReason);
        return false;
      }
      if (this.setSegmentCore(timelineSegmentId, segmentDataType, segment, ignoresReadonly)) {
        this.registerChange(changeSummary, footerMessage, { undoCallback, redoCallback });
      }
      return true;
    },

    setNewSegment(
      timelineDataId, segmentDataType, segment,
      footerMessage = 'New segment is set.',
      changeSummary = 'setting new segment',
      { undoCallback = undefined, redoCallback = undefined, ignoresReadonly = false } = {})
    {
      let timelineData = this.scattData.timelineDataSet[timelineDataId];
      let newSegmentId = timelineData.generateNewId();
      let newTimelineSegmentId = new TimelineSegmentId(timelineDataId, newSegmentId);
      this.setSegment(
        newTimelineSegmentId, segmentDataType, segment, footerMessage, changeSummary,
        { undoCallback, redoCallback, ignoresReadonly });
      return newTimelineSegmentId;
    },

    async createSegment(timelineDataId, begin, end) {
      let timelineData = this.scattData.timelineDataSet[timelineDataId];
      let emptySegment = timelineData.segmentDataType.generateEmpty(begin, end);
      let newSegmentId = timelineData.generateNewId();
      let newTimelineSegmentId = new TimelineSegmentId(timelineDataId, newSegmentId);
      let unsettableReason = this.getSegmentUnsettableReason(newTimelineSegmentId, timelineData.segmentDataType, emptySegment, false);
      if (unsettableReason !== undefined) {
        this.registerFooterMessage(unsettableReason);
        return undefined;
      }
      return await this.openDialog(
        'timeline-segment-editor',
        {
          timelineDataSet: this.scattData.timelineDataSet,
          minimumSegmentDurationMsec: minimumSegmentDuration.msec,
          targetSegment: emptySegment,
          targetTimelineSegmentId: newTimelineSegmentId,
          isImmutable: false,
          reopenPreviousSegmentEnabled: false,
          reopenNextSegmentEnabled: false,
          segmentContentDataInputDefinitions: this.mergedSegmentContentDataInputDefinitions,
          okCallback: (segmentEdited) => {
            this.setSegmentCore(newTimelineSegmentId, timelineData.segmentDataType, segmentEdited, false);
            this.registerChange('creating segment', 'Segment created.');
          },
        },
      );
    },

    deleteSegmentsCore(timelineSegmentIds, ignoresReadonly) {
      let referenceIdsToDelete = [];
      let deletionCount = 0;
      for (let timelineSegmentId of timelineSegmentIds) {
        if (!ignoresReadonly) {
          if (this.scattData.timelineDataSet.isTimelineSegmentImmutable(timelineSegmentId)) continue;
        }
        for (let [ referenceId, reference ] of Object.entries(referencePool.getAllReferences())) {
          if (reference.referFrom.isSameAs(timelineSegmentId) || reference.referTo.isSameAs(timelineSegmentId)) {
            referenceIdsToDelete.push(referenceId);
          }
        }
        ++deletionCount;
      }
      if (deletionCount > 0) {
        if (referenceIdsToDelete.length > 0) {
          this.unregisterReferences(...referenceIdsToDelete);
        }
        for (let timelineSegmentId of timelineSegmentIds) {
          let timelineDataId = timelineSegmentId.timelineDataId;
          let segmentId = timelineSegmentId.segmentId;
          this.$delete(this.scattData.timelineDataSet[timelineDataId].segments, segmentId);
        }
      }
      return deletionCount;
    },

    deleteSegments(timelineSegmentIds, { undoCallback = undefined, redoCallback = undefined, ignoresReadonly = false } = {}) {
      this.atomicChanges(
        'deleting segment',
        'Segment deleted.',
        () => {
          let deletionCount = this.deleteSegmentsCore(timelineSegmentIds, ignoresReadonly);
          if (deletionCount > 0) {
            this.clearSelectedTimelineSegments();
            this.registerChangeCore({ undoCallback, redoCallback });
          }
        },
      );
    },

    clearSegments(timelineSegmentIds) {
      for (let timelineSegmentId of timelineSegmentIds) {
        if (this.scattData.timelineDataSet.isTimelineSegmentImmutable(timelineSegmentId)) continue;
        let timelineData = this.scattData.timelineDataSet[timelineSegmentId.timelineDataId];
        let segment = this.scattData.timelineDataSet.getSegment(timelineSegmentId);
        let newEmptySegment = timelineData.segmentDataType.generateEmpty(segment.begin, segment.end);
        if (!segment.isSameAs(newEmptySegment)) {
          this.setSegmentCore(timelineSegmentId, timelineData.segmentDataType, newEmptySegment, false);
        }
      }
      this.registerChange('clearing segment', 'Segment cleared.');
    },

    divideSegment(timelineSegmentId, time) {
      if (!this.isTimelineSegmentIdSelected(timelineSegmentId)) {
        this.clearSelectedTimelineSegments();
        this.selectTimelineSegments(timelineSegmentId);
      }
      let dividedTimelineSegmentIds = [];
      for (let selectedTimelineSegmentId of this.selectedTimelineSegmentIds) {
        if (this.scattData.timelineDataSet.isTimelineSegmentImmutable(selectedTimelineSegmentId)) continue;
        let selectedTimelineSegment = this.scattData.timelineDataSet.getSegment(selectedTimelineSegmentId);
        let isTimeMsecEqualsToOrMoreThanSegmentBegin = (selectedTimelineSegment.begin === null)? true : time.isGreaterThanOrEqualTo(selectedTimelineSegment.begin);
        let isTimeMsecLessThanSegmentEnd = (selectedTimelineSegment.end === null)? true : time.isLessThan(selectedTimelineSegment.end);
        if (isTimeMsecEqualsToOrMoreThanSegmentBegin && isTimeMsecLessThanSegmentEnd) {
          let isSegmentDurationMsecFirstHalfEqualsToOrMoreThanMinimum =
            (selectedTimelineSegment.begin === null)? true : time.getOffsetFrom(selectedTimelineSegment.begin).isGreaterThanOrEqualTo(minimumSegmentDuration);
          let isSegmentDurationMsecSecondHalfEqualsToOrMoreThanMinimum =
            (selectedTimelineSegment.end === null)? true : selectedTimelineSegment.end.getOffsetFrom(time).isGreaterThanOrEqualTo(minimumSegmentDuration);
          if (!isSegmentDurationMsecFirstHalfEqualsToOrMoreThanMinimum || !isSegmentDurationMsecSecondHalfEqualsToOrMoreThanMinimum) continue;
          let dividedSegmentFirstHalf = selectedTimelineSegment;
          let dividedSegmentSecondHalf = selectedTimelineSegment.clone(false);
          dividedSegmentFirstHalf.assign({ end: time })
          dividedSegmentSecondHalf.assign({ begin: time });
          let selectedTimelineDataId = selectedTimelineSegmentId.timelineDataId;
          let selectedTimelineData = this.scattData.timelineDataSet[selectedTimelineDataId];
          let newSegmentIdSecondHalf = selectedTimelineData.generateNewId();
          let newTimelineSegmentId = new TimelineSegmentId(selectedTimelineDataId, newSegmentIdSecondHalf);
          this.setSegmentCore(newTimelineSegmentId, selectedTimelineData.segmentDataType, dividedSegmentSecondHalf, false);
          dividedTimelineSegmentIds.push(selectedTimelineSegmentId, newTimelineSegmentId);
        }
      }
      if (this.registerChange('dividing segment', 'Segment divided.')) {
        this.clearSelectedTimelineSegments();
        this.selectTimelineSegments(...dividedTimelineSegmentIds);
      }
    },

    mergeSegments(timelineSegmentIds) {
      let segmentIdsByTimelineDataId = {};
      for (let timelineSegmentId of timelineSegmentIds) {
        if (this.scattData.timelineDataSet.isTimelineSegmentImmutable(timelineSegmentId)) continue;
        let timelineDataId = timelineSegmentId.timelineDataId;
        let segmentId = timelineSegmentId.segmentId;
        if (!Object.keys(segmentIdsByTimelineDataId).includes(timelineDataId)) {
          segmentIdsByTimelineDataId[timelineDataId] = [];
        }
        segmentIdsByTimelineDataId[timelineDataId].push(segmentId);
      }
      this.atomicChanges(
        'merging segment',
        'Segment merged.',
        () => {
          {
            for (let [ timelineDataId, segmentIds ] of Object.entries(segmentIdsByTimelineDataId)) {
              if (segmentIds.length < 2) continue;
              let timelineData = this.scattData.timelineDataSet[timelineDataId];
              let segmentIdsSortedByBegin = segmentIds.toSorted(timelineData.getBeginTimeAscendingOrderIdFunction());
              let [ mergedSegmentId, ...deletingSegmentIds ] = segmentIdsSortedByBegin;
              let mergedTimelineSegmentId = new TimelineSegmentId(timelineDataId, mergedSegmentId);
              for (let deletingSegmentId of deletingSegmentIds) {
                timelineData.segments[mergedSegmentId].combine(timelineData.segments[deletingSegmentId]);
                let deletingTimelineSegmentId = new TimelineSegmentId(timelineDataId, deletingSegmentId);
                moveReferences(deletingTimelineSegmentId, mergedTimelineSegmentId);
              }
              let deletingTimelineSegmentIds = deletingSegmentIds.map(segmentId => new TimelineSegmentId(timelineDataId, segmentId));
              this.deleteSegmentsCore(deletingTimelineSegmentIds);
            }
            this.registerChangeCore();
          }

          function moveReferences(timelineSegmentIdMoveFrom, timelineSegmentIdMoveTo) {
            for (let reference of Object.values(referencePool.getAllReferences())) {
              if (reference.referFrom.isSameAs(timelineSegmentIdMoveFrom)) {
                reference.referFrom = timelineSegmentIdMoveTo;
              }
              if (reference.referTo.isSameAs(timelineSegmentIdMoveFrom)) {
                reference.referTo = timelineSegmentIdMoveTo;
              }
            }
          }
        },
      );
    },

    generateEmptySegment(timelineDataId, begin, end) {
      let emptyTimelineSegmentId = this.generateAndSetEmptySegmentCore(timelineDataId, begin, end, false);
      if (emptyTimelineSegmentId !== undefined) {
        this.registerChange('generating empty segment', 'Empty segment added.');
      }
      return emptyTimelineSegmentId;
    },

    generateAndSetEmptySegmentCore(timelineDataId, begin, end, ignoresReadonly) {
      let timelineData = this.scattData.timelineDataSet[timelineDataId];
      let segmentDataType = timelineData.segmentDataType;
      let newSegmentId = timelineData.generateNewId();
      let newTimelineSegmentId = new TimelineSegmentId(timelineDataId, newSegmentId);
      let newSegment = segmentDataType.generateEmpty(begin, end);
      let unsettableReason = this.getSegmentUnsettableReason(newTimelineSegmentId, segmentDataType, newSegment, ignoresReadonly);
      if (unsettableReason !== undefined) {
        this.registerFooterMessage(unsettableReason);
        return undefined;
      }
      this.setSegmentCore(newTimelineSegmentId, segmentDataType, newSegment, ignoresReadonly);
      return newTimelineSegmentId;
    },

    generateAndAttachEmptyNote(
      timelineSegmentIdReferTo, begin, end,
      footerMessage = 'Empty note is generated.',
      changeSummary = 'generating empty note',
    ) {
      let emptyNoteTimelineSegmentId = this.generateEmptyNoteCore(begin, end);
      this.registerChange(changeSummary, footerMessage);
      this.registerReferences(new Reference('Note', emptyNoteTimelineSegmentId, timelineSegmentIdReferTo));
    },

    generateEmptyNoteCore(begin, end) {
      if (!this.timelineDataIdOfNoteByCurrentUser) {
        this.generateNoteTimelineDataByCurrentUser();
      }
      return this.generateAndSetEmptySegmentCore(this.timelineDataIdOfNoteByCurrentUser, begin, end, false);
    },

    generatePlaybackHistory(begin, end) {
      if (!this.segmentDataTypes.includes(PlaybackHistorySegmentData)) return;
      if (!this.timelineDataIdOfPlaybackHistoryByCurrentUser) {
        this.generatePlaybackHistoryTimelineDataByCurrentUser();
      }
      return this.generateAndSetEmptySegmentCore(this.timelineDataIdOfPlaybackHistoryByCurrentUser, begin, end, true);
    },

    undoChange() {
      let result = changeHistory.undo(this.scattData);
      if (result) {
        this.registerFooterMessage(`Undo ${result.summary} successful.`);
        this.scattData = result.scattData;
        this.updateChangeHistoryStatus();
      } else {
        this.registerFooterMessage('Nothing to undo.');
      }
    },

    redoChange() {
      let result = changeHistory.redo(this.scattData);
      if (result) {
        this.registerFooterMessage(`Redo ${result.summary} successful.`);
        this.scattData = result.scattData;
        this.updateChangeHistoryStatus();
      } else {
        this.registerFooterMessage('Nothing to redo.');
      }
    },

    registerChangeCore({ changeSummary = 'change', undoCallback = undefined, redoCallback = undefined} = {}) {
      if (changeHistory.registerChange(this.scattData, changeSummary, undoCallback, redoCallback)) {
        this.updateChangeHistoryStatus();
        return true;
      }
      return false;
    },

    registerChange(changeSummary, footerMessage, { undoCallback = undefined, redoCallback = undefined } = {}) {
      if (this.registerChangeCore({ changeSummary, undoCallback, redoCallback })) {
        if (!changeHistory.areAtomicChangesStarted()) this.registerFooterMessage(footerMessage);
        return true;
      }
      return false;
    },

    atomicChanges(changeSummary, footerMessage, callback, { undoCallback = undefined, redoCallback = undefined } = {}) {
      changeHistory.beginAtomicChanges();
      callback();
      if (changeHistory.endAtomicChanges(changeSummary, undoCallback, redoCallback)) {
        this.updateChangeHistoryStatus();
        this.registerFooterMessage(footerMessage);
      }
    },

    updateChangeHistoryStatus() {
      this.isCurrentScattDataSameAsLastSaved = changeHistory.isSameAsLastSaved();
      this.isUndoTargetEmpty = changeHistory.isFirstChange();
      this.isRedoTargetEmpty = changeHistory.isLastChange();
    },

    registerFooterMessage(message) {
      this.footerMessages.push(message);
    },

    async showViewFilter() {
      return await this.openDialog(
        'view-filter',
        {
          showVideoAndEditorView: this.loadedConfig.showVideoAndEditorView,
          showTimelineView: this.loadedConfig.showTimelineView,
          okCallback: (showVideoAndEditorView, showTimelineView) => {
            this.loadedConfig.showVideoAndEditorView = showVideoAndEditorView;
            this.loadedConfig.showTimelineView = showTimelineView;
          },
        },
      );
    },

    async showDurationEditor() {
      return await this.openDialog(
        'timeline-view-range-editor',
        {
          timelineViewDurationMinMsec: minimumTimelineViewDuration.msec,
          timelineViewBeginMinMsec: this.timelineViewBeginMin.msec,
          timelineViewEndMaxMsec: this.timelineViewEndMax.msec,
          videoDurationMsec: this.videoDuration.msec,
          okCallback: ({ timelineViewBeginMinMsec, timelineViewEndMaxMsec }) => {
            this.timelineViewBeginMin.msec = timelineViewBeginMinMsec;
            this.timelineViewEndMax.msec = timelineViewEndMaxMsec;
            this.registerChange(
              'updating timeline view range',
              'Timeline view range is updated.',
            );
          },
        },
      );
    },

    async showHelp() {
      return await this.openDialog('help-message');
    },

    async showVersion() {
      return await this.openDialog(
        'general-message',
        {
          title: toolTitle,
          messages: [ `Version: ${toolSemanticVersionString}` ],
        }
      );
    },

    async showFooterMessageLogs() {
      return await this.openDialog(
        'general-message',
        {
          title: 'Messages',
          messages: this.footerMessages.logMessages,
        }
      );
    },

    emitBeforeUnload(beforeUnloadEvent) {
      this.$emit('before-unload', beforeUnloadEvent, this.isCurrentScattDataSameAsLastSaved);
    },

    copySegmentData(copySourceTimelineSegmentId, copyTargetTimelineSegmentIds) {
      {
        let sourceTimelineSegmentDataType = getTimelineSegmentDataType.call(this, copySourceTimelineSegmentId);
        if (sourceTimelineSegmentDataType === undefined) return;
        let targetTimelineSegmentDataType = getSingleTimelineSegmentDataType.call(this, copyTargetTimelineSegmentIds)
        if (targetTimelineSegmentDataType === undefined) return;
        if (sourceTimelineSegmentDataType !== targetTimelineSegmentDataType) return;
        let copySourceSegment = this.scattData.timelineDataSet.getSegment(copySourceTimelineSegmentId);
        for (let copyTargetTimelineSegmentId of copyTargetTimelineSegmentIds) {
          let copyTargetSegment = this.scattData.timelineDataSet.getSegment(copyTargetTimelineSegmentId);
          let newCopyTargetSegment = copyTargetSegment.clone(false);
          newCopyTargetSegment.data = copySourceSegment.data.clone();
          this.setSegmentCore(copyTargetTimelineSegmentId, targetTimelineSegmentDataType, newCopyTargetSegment, false);
        }
        this.registerChange('copying segment data', 'Segment data copied.');
      }

      function getTimelineSegmentDataType(timelineSegmentId) {
        let timelineData = this.scattData.timelineDataSet[timelineSegmentId.timelineDataId];
        if (!timelineData) return undefined;
        return timelineData.segmentDataType;
      }

      function getSingleTimelineSegmentDataType(timelineSegmentIds) {
        if (timelineSegmentIds.length === 0) return undefined;
        let singleSegmentDataType = getTimelineSegmentDataType.call(this, timelineSegmentIds[0]);
        for (let timelineSegmentId of timelineSegmentIds) {
          let currentSegmentDataType = getTimelineSegmentDataType.call(this, timelineSegmentId);
          if (currentSegmentDataType !== singleSegmentDataType) return undefined;
        }
        return singleSegmentDataType;
      }
    },

    copySegmentsFromCopySource() {
      if (this.timelineSegmentIdCopySource !== undefined) {
        if (this.scattData.timelineDataSet.getSegment(this.timelineSegmentIdCopySource)) {
          if (!this.isAnySelectedTimelineSegmentImmutable) {
            this.copySegmentData(this.timelineSegmentIdCopySource, this.selectedTimelineSegmentIds);
          }
        } else {
          this.timelineSegmentIdCopySource = undefined;
        }
      }
    },

    enableInputEventListener() {
      window.addEventListener('wheel', this.onScroll, { passive: false });
      window.addEventListener('keydown', this.onKeydown, { passive: false });
    },

    disableInputEventListener() {
      window.removeEventListener('wheel', this.onScroll);
      window.removeEventListener('keydown', this.onKeydown);
    },

    registerSelectSegmentsCallback(callback) {
      this.selectSegmentsCallback = callback;
      this.registerFooterMessage('Select-segment callback registered.');
    },

    unregisterSelectSegmentsCallback() {
      this.selectSegmentsCallback = undefined;
      this.registerFooterMessage('Select-segment callback unregistered.');
    },

    registerOnPlayInfoUpdatedCallback(callback) {
      let callbackId = `${++this.onPlayInfoUpdatedCallbackNextId}`;
      this.$set(this.onPlayInfoUpdatedCallbacks, callbackId, callback);
      return callbackId;
    },

    unregisterOnPlayInfoUpdatedCallback(callbackId) {
      this.$delete(this.onPlayInfoUpdatedCallbacks, callbackId);
    },

    registerReferences(...referenceIds) {
      referencePool.registerReferences(...referenceIds);
    },

    unregisterReferences(...referenceIds) {
      referencePool.unregisterReferences(...referenceIds);
    },

    seek(playTime) {
      if ((playTime === null) || (playTime.isLessThan(this.timelineViewBeginMin))) {
        playTime = this.timelineViewBeginMin;
      }
      this.playTime.assign(playTime);
      this.$refs.videoAndEditorView.seek(playTime);
    },

    setVideoAndEditorViewHeightPx(videoAndEditorViewHeightPx) {
      this.loadedConfig.videoAndEditorViewHeightPx = utils.clamp(
        videoAndEditorViewHeightPx,
        videoAndEditorViewHeightPxMin,
        this.$el.clientHeight - videoAndEditorViewBottomMarginPxMin,
      );
    },

    generateNewTimelineDataCore(timelineSegmentDataType, newTimelineName, isReadonly) {
      let newTimelineDataId = this.scattData.timelineDataSet.generateNewId();
      let newTimelineData = this.scattData.timelineDataSet.generateEmptyTimelineData(
        newTimelineName,
        timelineSegmentDataType,
        this.annotatorId,
        this.annotatorName,
        { readonly: isReadonly },
      );
      this.$set(this.scattData.timelineDataSet, newTimelineDataId, newTimelineData);
      return newTimelineDataId;
    },

    async openDialog(dialogComponentName, dialogProps = {}) {
      return await this.$refs.dialogManager.open(dialogComponentName, dialogProps);
    },

    async loadVideoDataFromLocalFile({ generatesWaveformDigestData = false, videoTitle = undefined } = {}) {
      let file = await openFileByDialog('video/*');
      let fileReader = new FileReader();
      fileReader.readAsArrayBuffer(file);
      fileReader.onload = () => {
        this.loadVideoData(new Uint8Array(fileReader.result), { generatesWaveformDigestData, videoTitle });
      };
    },

    async loadVideoFile(videoFilePath, { generatesWaveformDigestData = false, videoTitle = undefined } = {}) {
      if (videoTitle === undefined) videoTitle = getBasename(videoFilePath);
      let videoSource = await fetchFileAsUint8Array(videoFilePath);
      if (videoSource === undefined) return;
      this.loadVideoData(videoSource, { generatesWaveformDigestData, videoTitle });
    },

    loadVideoData(videoSource, { generatesWaveformDigestData = false, videoTitle = undefined } = {}) {
      this.mediaLoadingStatus = mediaLoadingStatus.loading;
      this.videoTitle = videoTitle;
      this.loadVideoUrl(URL.createObjectURL(
        new Blob([
          videoSource.buffer.slice(
            videoSource.byteOffset,
            videoSource.byteOffset + videoSource.length,
          ),
        ]),
      ));
      if (generatesWaveformDigestData) {
        this.loadWaveformDigestDataFromVideoData(videoSource);
      }
      this.mediaLoadingStatus = mediaLoadingStatus.loaded;
      this.registerFooterMessage('Video data is loaded.');
    },

    loadVideoUrl(videoUrl) {
      this.videoUrl = videoUrl;
    },

    async loadWaveformDigestDataFromLocalFile() {
      let file = await openFileByDialog();
      let fileReader = new FileReader();
      fileReader.readAsArrayBuffer(file);
      fileReader.onload = () => {
        this.loadWaveformDigestData(new Uint8Array(fileReader.result));
      };
    },

    async loadWaveformDigestFile(waveformDigestFilePath) {
      let waveformDigestData = await fetchFileAsUint8Array(waveformDigestFilePath);
      if (waveformDigestData === undefined) return;
      this.loadWaveformDigestData(waveformDigestData);
    },

    async loadWaveformDigestDataFromVideoData(videoData) {
      let audioContext = new AudioContext();
      let audioBuffer = await audioContext.decodeAudioData(
        videoData.buffer.slice(
          videoData.byteOffset,
          videoData.byteOffset + videoData.length,
        ),
      );
      this.registerFooterMessage('Generating waveform digest data...');
      try {
        let waveformDigestData = await WaveformDigest.generateDataFromAudioBufferByWebAssembly(audioBuffer, this.webAssemblyFileLocation, this.lightWaveform);
        this.$emit('generate-waveform-digest-data', waveformDigestData);
        this.loadWaveformDigestData(waveformDigestData);
      } catch (error) {
        if ((error instanceof WebAssembly.RuntimeError) || (error instanceof WaveformDigest.RuntimeError)) {
          logger.warn(error);
          this.loadWaveformDigest(WaveformDigest.loadFromAudioBuffer(audioBuffer));
        } else {
          logger.error(error);
        }
      }
      audioContext.close();
    },

    loadWaveformDigestData(waveformDigestData) {
      this.loadWaveformDigest(WaveformDigest.load(waveformDigestData));
    },

    loadWaveformDigest(waveformDigest) {
      this.waveformDigest = waveformDigest;
      this.registerFooterMessage('Waveform digest data is loaded.');
    },

    async loadScattDataFromLocalFile() {
      let file = await openFileByDialog('.json, application/json');
      let fileReader = new FileReader();
      fileReader.readAsText(file);
      fileReader.onload = () => {
        this.loadScattData(JSON.parse(fileReader.result));
      };
    },

    async loadScattFile(scattDataFilePath) {
      let scattDataRawObj = await fetchFileAsJson(scattDataFilePath);
      if (scattDataRawObj === undefined) return;
      this.loadScattData(scattDataRawObj);
    },

    loadScattData(scattDataRawObj) {
      this.unregisterReferences(...Object.keys(referencePool.getAllReferences()));
      this.registerReferences(...ScattData.loadReferences(scattDataRawObj));
      this.scattData = new ScattData(scattDataRawObj, this.mergedSegmentDataTypes);
      this.registerChange('loading scatt data', 'Scatt data loaded.');
    },

    downloadScattData() {
      let scattDataJsonString = JSON.stringify(this.getCurrentScattData());
      let scattDataBlob = new Blob([ scattDataJsonString ]);
      let anchorElement = document.createElement('a');
      anchorElement.href = URL.createObjectURL(scattDataBlob, { type: 'application/json; charset=utf-8' });
      anchorElement.download = scattDataFileName;
      anchorElement.click();
    },

    saveScattData() {
      changeHistory.markAsSaved();
      this.isCurrentScattDataSameAsLastSaved = changeHistory.isSameAsLastSaved();
      let scattDataRawObj = this.getCurrentScattData();
      this.$emit('save', scattDataRawObj);
      return scattDataRawObj;
    },

    createNewScattData() {
      this.loadScattData(ScattData.generateEmpty().generateRawObj());
    },

    getCurrentScattData() {
      return generateScattDataRawObj(this.scattData);
    },
  },
};
</script>
